import {
  Car, Container, Truck, PackageOpen, Plane, FileCheck,
  ShieldCheck, BadgeDollarSign, Timer, Lock, Radar, Users,
} from "lucide-react";

export const ICONS = {
  Car, Container, Truck, PackageOpen, Plane, FileCheck,
  ShieldCheck, BadgeDollarSign, Timer, Lock, Radar, Users,
};

// Ordered list used by the CMS icon pickers
export const ICON_NAMES = Object.keys(ICONS);

export const DEFAULTS = {
  hero: {
    badge: "We Ship From the USA to Ghana",
    title: "Shipping Cars & Cargo from the",
    titleHighlight: "USA to Ghana",
    subtitle: "Reliable, affordable & secure shipping services between America and Ghana — vehicles, containers, towing and cargo, handled end-to-end.",
    primaryCta: "Get a Quote",
    secondaryCta: "Contact Us on WhatsApp",
    stats: [
      { value: "5+", label: "US Departure Ports" },
      { value: "100%", label: "Secure Handling" },
      { value: "16+ days", label: "To Tema Port" },
    ],
  },
  about: {
    eyebrow: "About R.O.K Motors",
    title: "Your trusted bridge between America and Ghana",
    paragraphs: [
      "R.O.K Motors is a trusted international shipping and logistics company specializing in vehicle transportation, container shipping, towing services, and cargo logistics from the United States to Ghana.",
      "We are committed to providing safe, reliable, and affordable shipping solutions with exceptional customer service — from the moment you request a quote to the day you collect your shipment at Tema Port.",
    ],
    points: [
      "Vehicle transportation & luxury car shipping",
      "20ft & 40ft container shipping",
      "Nationwide US towing before shipment",
      "Customs clearance support at Tema Port",
    ],
  },
  services: {
    eyebrow: "What We Do",
    title: "Complete shipping & logistics services",
    desc: "From a single luxury vehicle to full container loads and urgent air freight — we handle it all.",
    items: [
      { icon: "Car", title: "Vehicle Shipping", desc: "Safe transportation of sedans, SUVs, trucks, luxury vehicles, motorcycles, and heavy equipment." },
      { icon: "Container", title: "Container Shipping", desc: "20ft and 40ft container shipping from the USA to Ghana with full and shared load options." },
      { icon: "Truck", title: "Towing Services", desc: "Professional towing and transport throughout the United States before shipment." },
      { icon: "PackageOpen", title: "Cargo Shipping", desc: "Personal effects, commercial goods, household items, and business equipment." },
      { icon: "Plane", title: "Air Freight", desc: "Fast international air shipping for urgent and time-sensitive deliveries." },
      { icon: "FileCheck", title: "Customs Assistance", desc: "We help customers clear cargo smoothly and quickly at Tema Port, Ghana." },
    ],
  },
  routes: {
    eyebrow: "Shipping Routes",
    title: "Direct ocean routes from the USA to Tema",
    desc: "We ship from major US ports straight to Tema Port, Ghana.",
    items: [
      { from: "New York / New Jersey", to: "Tema", days: "18–25 days" },
      { from: "Los Angeles", to: "Tema", days: "28–35 days" },
      { from: "Seattle", to: "Tema", days: "30–38 days" },
      { from: "Houston", to: "Tema", days: "22–30 days" },
      { from: "Savannah", to: "Tema", days: "16–22 days" },
    ],
  },
  whyUs: {
    eyebrow: "Why Choose Us",
    title: "Shipping you can actually rely on",
    desc: "Thousands of vehicles and cargo shipments delivered safely from the USA to Ghana.",
    items: [
      { icon: "ShieldCheck", title: "Reliable Shipping", desc: "Trusted schedules and dependable delivery, shipment after shipment." },
      { icon: "BadgeDollarSign", title: "Affordable Pricing", desc: "Transparent, competitive rates with no hidden fees." },
      { icon: "Timer", title: "Fast Delivery", desc: "Optimized routes to get your vehicle and cargo to Ghana quickly." },
      { icon: "Lock", title: "Secure Vehicle Handling", desc: "Careful loading, lashing, and handling of every vehicle." },
      { icon: "Radar", title: "Cargo Tracking", desc: "Track your shipment online from pickup to arrival at Tema Port." },
      { icon: "Users", title: "Experienced Team", desc: "A seasoned logistics team managing your shipment end-to-end." },
    ],
  },
  steps: {
    eyebrow: "How It Works",
    title: "Four simple steps to Ghana",
    items: [
      { n: "01", title: "Request a Quote", desc: "Tell us what you're shipping and where from. Get a fast, free quote." },
      { n: "02", title: "Drop Off Vehicle or Cargo", desc: "Deliver to our US facility, or we'll tow it for you." },
      { n: "03", title: "We Handle Shipping", desc: "Loading, documentation, customs, and ocean or air freight." },
      { n: "04", title: "Receive in Ghana", desc: "Clear and collect your shipment at Tema Port with our help." },
    ],
  },
  gallery: {
    eyebrow: "Gallery",
    title: "Our operations in motion",
    desc: "Luxury cars, tow trucks, containers, cargo ships and the Port of Tema.",
    items: [
      { url: "https://images.unsplash.com/photo-1670121180583-39ab653a071c", alt: "Cargo ship at port", tall: true },
      { url: "https://images.unsplash.com/photo-1698998882426-39a6609ab10a", alt: "Tow truck with vehicle on flatbed", tall: false },
      { url: "https://images.unsplash.com/photo-1568723331958-ce8c291349e7", alt: "Luxury SUV", tall: false },
      { url: "https://images.unsplash.com/photo-1700777685830-f501e67260e6", alt: "Crane loading shipping container", tall: true },
      { url: "https://images.unsplash.com/photo-1613690399151-65ea69478674", alt: "Container ship on the ocean", tall: false },
      { url: "https://images.unsplash.com/photo-1706499856012-14f062c72b49", alt: "Stacked shipping containers", tall: false },
      { url: "https://images.unsplash.com/photo-1542296332-2e4473faf563", alt: "Air cargo aircraft loading", tall: true },
      { url: "https://images.unsplash.com/photo-1567604804217-9be3addbd802", alt: "Luxury Mercedes vehicle", tall: false },
    ],
  },
  testimonials: {
    eyebrow: "Testimonials",
    title: "Trusted by shippers across two continents",
    items: [
      { name: "Kwame A.", location: "Accra, Ghana", text: "My vehicle arrived safely in Tema exactly as promised. Professional from start to finish." },
      { name: "Sarah M.", location: "Houston, USA", text: "Excellent communication and affordable pricing. I always knew where my shipment was." },
      { name: "Daniel O.", location: "Kumasi, Ghana", text: "Highly recommended for shipping cars to Ghana. Smooth customs clearance at Tema Port." },
    ],
  },
  faqs: {
    eyebrow: "FAQ",
    title: "Frequently asked questions",
    items: [
      { q: "How long does shipping take?", a: "Ocean freight typically takes between 16 and 38 days depending on the US departure port. Air freight is much faster for urgent deliveries. We provide an estimated delivery window with every quote." },
      { q: "How much does it cost?", a: "Cost depends on the vehicle or cargo size, the departure port, and shipping method (container vs. RoRo vs. air). Request a free quote and we'll give you a transparent, all-in price." },
      { q: "Can I ship personal items inside my vehicle?", a: "In many cases yes, within allowed limits. We'll advise you on what can be shipped inside the vehicle to stay compliant with customs regulations." },
      { q: "Do you offer container shipping?", a: "Yes. We offer both 20ft and 40ft containers, as well as shared container space for smaller shipments." },
      { q: "Can I track my shipment?", a: "Absolutely. Every shipment gets a tracking ID you can enter on our website to see its current status and location." },
      { q: "Do you provide customs assistance?", a: "Yes. We help you clear your vehicle or cargo at Tema Port quickly and correctly, reducing delays and demurrage." },
    ],
  },
  contact: {
    eyebrow: "Contact Us",
    title: "Let's get your shipment moving",
    desc: "Reach out any time — we're here to help you ship from the USA to Ghana.",
    whatsapp: "0506424358",
    whatsapp2: "+233 55 747 6562",
    email: "r.o.k.motors.limited@gmail.com",
    location: "USA & Tema, Ghana",
  },
  cta: {
    badge: "Own Your Power To Control",
    title: "Ready to ship your car or cargo to Ghana?",
    desc: "Get a free, no-obligation quote today. Our team responds fast with a transparent, all-in price.",
  },
  footer: {
    description: "Reliable, affordable and secure vehicle, container and cargo shipping from the USA to Ghana.",
    tagline: "Own Your Power To Control",
  },
};
